import React from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Checkbox,
  Button,
  DialogActions,
  TextField,
  DialogContent
} from '@material-ui/core';
import { useSelector } from 'react-redux';
import { RESTRICTED_ACCESS } from '../../constants/restrictedAccessConstants';
import { createOrgIdBrandsMappingReverse, getNewBrand } from '../../common/components/util';
import DeleteDocumentDialog from '../../common/components/deleteDocumentDialog';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    padding: 10,
  },
  title: {
    flex: '1 1 100%',
    fontSize: 25,
    fontWeight: 'bold',
    marginLeft: '0.7rem'
  },
  tableHeader: {
    color: "#133F8A",
    fontWeight: 900,
    fontSize: 12,
    fontFamily: "Nunito",
    minWidth: 60,
  },
  offer: {
    paddingRight: 30,
    borderRight: '3px solid grey',
    fontWeight: 'bold',
    color: '#999999',
  }
}));

export default function ViewAnnexurePartList(props: any) {
  const classes = useStyles();
  const { getAllAnnexureListData }: any = useSelector((state: any) => state.annexure);
  const showCreateChangeEnquiry = !sessionStorage.getItem('restrictFeat')?.includes(RESTRICTED_ACCESS.CREATE_CHANGE_ENQUIRY);
  const orgIdBrandsMappingReverse: any = {
    "Al Futtaim Motors": "2001",
    "Honda": "2002",
    "Trading Enterprises": "2380",
    "ALAC AfterMarket Parts": "2195",
    "ALAC Tyres & Batteries": "2195",
    BYD: "2092"
  }
  const [checkedAll, setcheckedAll] = React.useState<boolean>(false);
  const [searchText, setSearchText] = React.useState<string>('');
  const [selectedItems, setSelectedItems] = React.useState<string[]>([]);
  const [productList, setProductList] = React.useState<any[]>([]);
  const [showCloseConfirm, setShowCloseConfirm] = React.useState(false);
  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;

    if (isChecked) {
      const allItems = productList.map((item) => item.partNumber);
      setSelectedItems(allItems);
    } else {
      setSelectedItems([]);
    }
  };

  React.useEffect(() => {
    setSelectedItems([]);
  }, [searchText]);

  React.useEffect(() => {
    if (getAllAnnexureListData?.data?.data?.productData) {
      const updatedList =
        getAllAnnexureListData.data.data.productData.map(
          (item: any) => ({
            ...item,
            quantity: "",
            error: "",
          })
        );

      setProductList(updatedList);
    }
  }, [getAllAnnexureListData]);
  const handleCheckboxClick = (partNumber: string) => {
    const isSelected = selectedItems.includes(partNumber);
    const updatedSelectedItems = isSelected
      ? selectedItems.filter(id => id !== partNumber)
      : [...selectedItems, partNumber];

    setSelectedItems(updatedSelectedItems);

    if (!isSelected && updatedSelectedItems.length === productList.length) {
      setcheckedAll(true);
    } else {
      setcheckedAll(false);
    }

    setProductList(prev =>
      prev.map(item =>
        item.partNumber === partNumber
          ? {
            ...item,
            error: isSelected ? '' : item.error,
          }
          : item
      )
    );
  };

  const getBrandTier = (brandPricingTier: any[]) => {

    const allowedTiers = ["gold", "platinum", "elite"];

    // Take first tier object directly
    const tierValue = brandPricingTier?.[0]?.tier;

    if (!tierValue) return null;

    const extractedTier = tierValue
      .split("-")
      .pop()
      ?.trim()
      .toLowerCase();

    // Only allow gold / platinum / elite
    return allowedTiers.includes(extractedTier)
      ? extractedTier
      : null;
  };

  const handleQuantityChange = (event: React.ChangeEvent<HTMLInputElement>, partNumber: string) => {
    const value = event.target.value;
    const updatedList = productList.map((item) => {
      if (item.partNumber === partNumber) {
        const qty = Number(value);
        let error = '';

        const minQty = item.quantityTierRange?.tier_1?.minQty || 0;
        const tier3MaxQty = item.quantityTierRange?.tier_3?.maxQty;
        const maxQty = tier3MaxQty && !isNaN(tier3MaxQty) ? tier3MaxQty : 99999;

        if (value && qty < minQty) {
          error = `Quantity value should be greater or equal to ${minQty}`;
        } else if (value && qty > maxQty) {
          if (tier3MaxQty) {
            error = `Quantity value should be less than or equal to ${maxQty}`;
          } else {
            error = `Quantity value should be a valid 5 digit number`;
          }
        }

        return {
          ...item,
          quantity: value,
          error,
        };
      }
      return item;
    });

    setProductList(updatedList);

    const matched = updatedList.find((x) => x.partNumber === partNumber);
    if (matched && !matched.error && matched.quantity !== '') {
      setSelectedItems((prev) => [...new Set([...prev, partNumber])]);
    } else {
      setSelectedItems((prev) => prev.filter((id) => id !== partNumber));
    }
  };

  React.useEffect(() => {
    if (productList.length > 0 && selectedItems.length === productList.length) {
      setcheckedAll(true);
    } else {
      setcheckedAll(false);
    }
  }, [selectedItems, productList]);

  // Check if selected items have valid quantity & no error
  const isValidSelection = selectedItems.length > 0 && selectedItems.some(partNumber => {
    const item = productList.find(p => p.partNumber === partNumber);
    return item && item.quantity !== '' && !item.error;
  });

  return (
    <>
      <DialogContent className={classes.root} dividers
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '16px 24px'
        }}>
        <DeleteDocumentDialog
          open={showCloseConfirm}
          text="Are you sure you want to close the pop-up? Any unsaved data will be lost."
          handleopenCloseDocument={(data: any) => {
            if (data === 'delete') {
              props.setOpen(false);
              setShowCloseConfirm(false);
            }
            if (!data) setShowCloseConfirm(false)
          }} />
        <Toolbar className={clsx(classes.root)}>
          <div className="d-flex justify-content-end w-100">
            <h4 className="page-title mt-2">Load From Annexure/Core Product</h4>
            <div className="ml-auto">
              <TextField
                size="small"
                variant="outlined"
                placeholder="Search by Part No/Description/Brand Name"
                className="ml-3 commonRoundedInputs common-input-font-size w-300"
                label="Search by Part No/Description/Brand Name"
                value={searchText}
                onChange={(e: any) => setSearchText(e.target.value)}
              />
            </div>
          </div>
        </Toolbar>
        <TableContainer className="height-500">
          <Table aria-label="dealer table">
            <TableHead>
              {/* First Row */}
              <TableRow>
                <TableCell align="left" rowSpan={2}>
                  <Checkbox
                    checked={checkedAll}
                    onChange={handleSelectAllClick}
                    inputProps={{ 'aria-label': 'select all' }}
                  />
                </TableCell>

                <TableCell align="left" rowSpan={2}><div className="dealer-table-title">Part No.</div></TableCell>
                <TableCell align="left" rowSpan={2}><div className="dealer-table-title">Brand</div></TableCell>
                <TableCell align="left" rowSpan={2}><div className="dealer-table-title">Description</div></TableCell>

                <TableCell align="center" colSpan={3}>
                  <div className="dealer-table-title">Min Order Qty</div>
                </TableCell>

                <TableCell align="center" colSpan={3}>
                  <div className="dealer-table-title">Offered Price (Excl. VAT)</div>
                </TableCell>

                <TableCell align="left" rowSpan={2}><div className="dealer-table-title">Quantity</div></TableCell>
              </TableRow>

              {/* Second Row */}
              <TableRow>
                <TableCell align="center"><div className="dealer-table-title-desc">Tier 1</div></TableCell>
                <TableCell align="center"><div className="dealer-table-title-desc">Tier 2</div></TableCell>
                <TableCell align="center"><div className="dealer-table-title-desc">Tier 3</div></TableCell>

                <TableCell align="center"><div className="dealer-table-title-desc">Tier 1</div></TableCell>
                <TableCell align="center"><div className="dealer-table-title-desc">Tier 2</div></TableCell>
                <TableCell align="center"><div className="dealer-table-title-desc">Tier 3</div></TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {productList
                .filter((item) => {
                  const partNumberMatch = item.partNumber?.toLowerCase().includes(searchText.toLowerCase());
                  const descriptionMatch = item.description?.toLowerCase().includes(searchText.toLowerCase());
                  const brandMatch = getNewBrand(item.brand)?.toLowerCase().includes(searchText.toLowerCase());
                  return partNumberMatch || descriptionMatch || brandMatch;
                })

                // ✅ Step 2: filter out brands without gold/platinum/elite
                .filter((item) => {
                  const tier = getBrandTier(props.brandPricingTier);
                  return tier !== null; // show only if valid tier
                })
                .map((item, index) => {
                  const isSelected = selectedItems.includes(item.partNumber);
                  const qty = Number(item.quantity);
                  const tier1Min = item.quantityTierRange?.tier_1?.minQty || 0;
                  const tier2Min = item.quantityTierRange?.tier_2?.minQty || 0;
                  const tier3Min = item.quantityTierRange?.tier_3?.minQty || 0;

                  const highlightTier =
                    qty >= tier3Min
                      ? 'tier_3'
                      : qty >= tier2Min
                        ? 'tier_2'
                        : qty >= tier1Min
                          ? 'tier_1'
                          : null;
                  const priceType = getBrandTier(props.brandPricingTier);
                  // const priceType = props.pricingTier?.toLowerCase() || 'gold';

                  return (
                    <TableRow key={index}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleCheckboxClick(item.partNumber)}
                          className="m-1"
                        />
                      </TableCell>
                      <TableCell><div className="dealer-table-content">{item.partNumber}</div></TableCell>
                      <TableCell><div className="dealer-table-content">{item.brand ? getNewBrand(item.brand) : "-"}</div></TableCell>
                      <TableCell><div className="dealer-table-content pb-3 mt-2">{item.description || "TEST"}</div></TableCell>
                      <TableCell><div className="dealer-table-content pb-3">{item.quantityTierRange?.tier_1?.minQty || "-"}</div></TableCell>
                      <TableCell><div className="dealer-table-content pb-3">{item.quantityTierRange?.tier_2?.minQty || "-"}</div></TableCell>
                      <TableCell><div className="dealer-table-content pb-3">{item.quantityTierRange?.tier_3?.minQty || "-"}</div></TableCell>
                      <TableCell><div className={`dealer-table-content pb-3 ${highlightTier === 'tier_1' ? 'highlight-price' : ''}`}>{item.priceTierRange?.tier_1?.[priceType] || "-"}</div></TableCell>
                      <TableCell><div className={`dealer-table-content pb-3 ${highlightTier === 'tier_2' ? 'highlight-price' : ''}`}>{item.priceTierRange?.tier_2?.[priceType] || "-"}</div></TableCell>
                      <TableCell><div className={`dealer-table-content pb-3 ${highlightTier === 'tier_3' ? 'highlight-price' : ''}`}>{item.priceTierRange?.tier_3?.[priceType] || "-"}</div></TableCell>
                      <TableCell style={{ verticalAlign: "middle", minWidth: "150px" }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', width: '150px' }}>
                          <TextField
                            type="number"
                            size="small"
                            style={{ width: '80px', textAlign: 'center' }}
                            className="commonRoundedInputs"
                            variant="outlined"
                            value={item.quantity}
                            onChange={(e: any) => handleQuantityChange(e, item.partNumber)}
                          />
                          {item.error && (
                            <span className="quantity-error">{item.error}</span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
          {getAllAnnexureListData?.data?.data?.productData?.length === 0 && (
            <div className="my-5 mx-auto text-center">No data found.</div>
          )}
        </TableContainer>
      </DialogContent>
      <DialogActions className="mt-2 mb-3 justify-content-end dialogFooter" style={{
        padding: '12px 24px', justifyContent: 'flex-end'
      }}>
        {showCreateChangeEnquiry && (
          <Button
            autoFocus
            variant="contained"
            color="secondary"
            disabled={!isValidSelection}
            onClick={() => {
              let data: any[] = [];

              const validSelectedProducts = productList.filter(item =>
                selectedItems.includes(item.partNumber) &&
                item.quantity !== '' &&
                !item.error
              );

              if (validSelectedProducts.length > 0) {
                data = validSelectedProducts.map(item => ({
                  productId: item.partNumber,
                  quantity: item.quantity
                }));
                props.setOpen(false);
                props.commonInsertionLogic(data);
              }
            }}

            className={`font-size-11 mr-1 actionButtomButtomsBlue ${!isValidSelection ? 'actionButtomButtomsBlueDisabled' : ''}`}
          >
            Add to Order Enquiry
          </Button>
        )}
        <Button className="cancel-button font-size-11 mr-3" onClick={() => setShowCloseConfirm(true)}>
          Close
        </Button>
      </DialogActions>
    </>
  );
}
